import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import Icon from '../../../components/AppIcon';

const ClientCommunication = ({ clientData, projectId }) => {
  const [messageData, setMessageData] = useState({
    subject: '',
    message: '',
    priority: 'normal',
    requestType: 'question'
  });
  const [isSending, setIsSending] = useState(false);

  // Mock conversation history
  const conversationHistory = [
    {
      id: 1,
      sender: 'client',
      senderName: clientData?.name || 'Sarah Martinez',
      timestamp: '2024-11-15T14:30:00',
      subject: 'Project Timeline Clarification',
      message: 'Hi! I wanted to clarify the timeline for the project. Would it be possible to complete the work before the holidays? Also, do you provide temporary kitchen setup during renovation?',
      priority: 'normal'
    },
    {
      id: 2,
      sender: 'vendor',
      senderName: 'Your Business',
      timestamp: '2024-11-15T16:45:00',
      subject: 'Re: Project Timeline Clarification',
      message: 'Thank you for your inquiry! Yes, we can definitely work with a pre-holiday timeline. We typically provide a temporary kitchen setup in an adjacent room. I\'ll include this in my detailed quote. Could you let me know your preferred completion date?',
      priority: 'normal'
    },
    {
      id: 3,
      sender: 'client',
      senderName: clientData?.name || 'Sarah Martinez',
      timestamp: '2024-11-16T09:15:00',
      subject: 'Material Preferences Update',
      message: 'Perfect! Ideally, we\'d like completion by December 20th. I\'ve also been thinking about the countertops - would you be able to show samples of both quartz and granite options during your site visit?',
      priority: 'high'
    }
  ];

  const handleInputChange = (field, value) => {
    setMessageData(prev => ({ ...prev, [field]: value }));
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    setIsSending(true);

    try {
      // Simulate sending message
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      console.log('Message sent:', {
        projectId,
        clientEmail: clientData?.email,
        message: messageData
      });

      // Reset form
      setMessageData({
        subject: '',
        message: '',
        priority: 'normal',
        requestType: 'question'
      });
      
      // In real app, refresh conversation history
    } catch (error) {
      console.error('Failed to send message:', error);
    } finally {
      setIsSending(false);
    }
  };

  const formatTimestamp = (timestamp) => {
    return new Date(timestamp)?.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getPriorityIcon = (priority) => {
    switch (priority) {
      case 'high': return { icon: 'AlertCircle', color: 'text-destructive' };
      case 'medium': return { icon: 'AlertTriangle', color: 'text-warning' };
      default: return { icon: 'MessageCircle', color: 'text-primary' };
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
      {/* Client Information Panel */}
      <div className="lg:col-span-1 space-y-6">
        {/* Client Details */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
            <Icon name="User" size={18} className="mr-2" />
            Client Contact
          </h2>
          
          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-foreground text-sm mb-1">Name</h3>
              <p className="text-text-secondary">{clientData?.name}</p>
            </div>
            
            <div>
              <h3 className="font-semibold text-foreground text-sm mb-1">Email</h3>
              <div className="flex items-center space-x-2">
                <p className="text-text-secondary text-sm">{clientData?.email}</p>
                <Button variant="ghost" size="sm">
                  <Icon name="Copy" size={14} />
                </Button>
              </div>
            </div>
            
            <div>
              <h3 className="font-semibold text-foreground text-sm mb-1">Phone</h3>
              <div className="flex items-center space-x-2">
                <p className="text-text-secondary text-sm">{clientData?.phone}</p>
                <Button variant="ghost" size="sm">
                  <Icon name="Phone" size={14} />
                </Button>
              </div>
            </div>
          </div>
        </div>

        {/* Communication Guidelines */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
            <Icon name="Info" size={18} className="mr-2" />
            Communication Guidelines
          </h2>
          
          <div className="space-y-3 text-sm">
            <div className="flex items-start space-x-2">
              <Icon name="Clock" size={14} className="text-primary mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Response Time</p>
                <p className="text-text-secondary">Within 24 hours for normal inquiries</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-2">
              <Icon name="Mail" size={14} className="text-success mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Preferred Method</p>
                <p className="text-text-secondary">Email for project updates</p>
              </div>
            </div>
            
            <div className="flex items-start space-x-2">
              <Icon name="Phone" size={14} className="text-warning mt-0.5" />
              <div>
                <p className="font-medium text-foreground">Urgent Contact</p>
                <p className="text-text-secondary">Phone calls for urgent matters</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
            <Icon name="Zap" size={18} className="mr-2" />
            Quick Actions
          </h2>
          
          <div className="space-y-3">
            <Button variant="outline" className="w-full justify-start" iconName="Calendar" iconPosition="left">
              Schedule Site Visit
            </Button>
            <Button variant="outline" className="w-full justify-start" iconName="FileText" iconPosition="left">
              Request Additional Info
            </Button>
            <Button variant="outline" className="w-full justify-start" iconName="Camera" iconPosition="left">
              Request Photos
            </Button>
            <Button variant="outline" className="w-full justify-start" iconName="AlertTriangle" iconPosition="left">
              Report Issue
            </Button>
          </div>
        </div>
      </div>
      {/* Communication Panel */}
      <div className="lg:col-span-2 space-y-6">
        {/* Message Composer */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
            <Icon name="Edit" size={18} className="mr-2" />
            Send Message to Client
          </h2>
          
          <form onSubmit={handleSendMessage} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Message Type
                </label>
                <Select
                  value={messageData?.requestType}
                  onChange={(value) => handleInputChange('requestType', value)}
                  className="w-full"
                >
                  <option value="question">Question</option>
                  <option value="clarification">Clarification Request</option>
                  <option value="update">Project Update</option>
                  <option value="scheduling">Scheduling</option>
                  <option value="change-request">Change Request</option>
                </Select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Priority
                </label>
                <Select
                  value={messageData?.priority}
                  onChange={(value) => handleInputChange('priority', value)}
                  className="w-full"
                >
                  <option value="normal">Normal</option>
                  <option value="medium">Medium</option>
                  <option value="high">High Priority</option>
                </Select>
              </div>
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Subject *
              </label>
              <Input
                type="text"
                placeholder="Enter message subject..."
                value={messageData?.subject}
                onChange={(e) => handleInputChange('subject', e?.target?.value)}
                required
                className="w-full"
              />
            </div>
            
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                Message *
              </label>
              <textarea
                className="w-full min-h-[120px] p-3 border border-border rounded-md focus:ring-2 focus:ring-primary focus:border-transparent resize-vertical"
                placeholder="Type your message to the client..."
                value={messageData?.message}
                onChange={(e) => handleInputChange('message', e?.target?.value)}
                required
              />
            </div>
            
            <div className="flex items-center justify-between pt-2">
              <div className="text-sm text-text-secondary">
                <Icon name="Info" size={14} className="inline mr-1" />
                Messages are sent via the Stackd platform
              </div>
              
              <Button
                type="submit"
                variant="default"
                iconName="Send"
                iconPosition="left"
                disabled={isSending}
              >
                {isSending ? 'Sending...' : 'Send Message'}
              </Button>
            </div>
          </form>
        </div>

        {/* Conversation History */}
        <div className="bg-card border border-border rounded-xl p-6">
          <h2 className="text-lg font-bold text-foreground mb-4 flex items-center">
            <Icon name="MessageSquare" size={18} className="mr-2" />
            Conversation History
          </h2>
          
          <div className="space-y-4 max-h-[500px] overflow-y-auto">
            {conversationHistory?.map((message) => {
              const priorityInfo = getPriorityIcon(message?.priority);
              
              return (
                <div
                  key={message?.id}
                  className={`p-4 rounded-lg border ${
                    message?.sender === 'vendor'
                      ? 'bg-primary/5 border-primary/20 ml-8' :'bg-muted border-border mr-8'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <div className={`p-1 rounded-full ${
                        message?.sender === 'vendor' ? 'bg-primary/10' : 'bg-muted'
                      }`}>
                        <Icon 
                          name={message?.sender === 'vendor' ? 'User' : 'UserCheck'} 
                          size={14} 
                          className={message?.sender === 'vendor' ? 'text-primary' : 'text-text-secondary'}
                        />
                      </div>
                      <span className="font-medium text-foreground text-sm">
                        {message?.senderName}
                      </span>
                    </div>
                    
                    <div className="flex items-center space-x-2">
                      {message?.priority !== 'normal' && (
                        <Icon name={priorityInfo?.icon} size={14} className={priorityInfo?.color} />
                      )}
                      <span className="text-xs text-text-secondary">
                        {formatTimestamp(message?.timestamp)}
                      </span>
                    </div>
                  </div>
                  
                  <h3 className="font-medium text-foreground mb-2 text-sm">
                    {message?.subject}
                  </h3>
                  
                  <p className="text-text-secondary text-sm leading-relaxed">
                    {message?.message}
                  </p>
                  
                  <div className="flex items-center justify-end mt-3 space-x-2">
                    <Button variant="ghost" size="sm">
                      <Icon name="Reply" size={14} />
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
          
          {conversationHistory?.length === 0 && (
            <div className="text-center py-8 text-text-secondary">
              <Icon name="MessageSquare" size={32} className="mx-auto mb-2 opacity-50" />
              <p>No messages yet. Start the conversation with your client!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ClientCommunication;